import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, Sparkles, AlertTriangle, XCircle, CheckCircle2 } from 'lucide-react';
import type {
  NormalizedThinkingEvent,
  MessageExecutionStatus,
  ThinkingMetrics,
} from './types';
import { ThinkingStepList } from './ThinkingStepList';
import { formatDuration } from './normalizeThinkingEvent';

interface ThinkingPanelProps {
  events: NormalizedThinkingEvent[];
  metrics: ThinkingMetrics;
  status: MessageExecutionStatus;
  isStreaming: boolean;
  thinkingComplete?: boolean;
  streamPhase?: 'thinking' | 'final';
  warningCount: number;
  toolsUsedCount: number;
  toolsUsed: string[];
  currentStepName?: string;
  provider?: string;
  model?: string;
}

const TOOL_NOUNS: Record<string, string> = {
  get_patient_context: 'patient context',
  get_patient_demographics: 'demographics',
  get_medications: 'medications',
  get_conditions: 'conditions',
  get_observations: 'lab results',
  get_encounters: 'encounters',
  get_allergies: 'allergies',
  get_procedures: 'procedures',
  get_immunizations: 'immunizations',
  get_diagnostic_reports: 'diagnostic reports',
  get_care_plans: 'care plans',
  // #158: was missing, so an otherwise-identical "Reviewed <resource>"
  // summary fell back to the raw tool id ("Used get family history")
  // instead of matching every other chart-resource tool's grammar.
  get_family_history: 'family history',
  get_fhir_patient: 'patient record',
  get_redflags: 'red flags',
  get_latest_assessment: 'latest assessment',
  march_rag_retrieval: 'clinical references',
  multi_agent_reasoning: 'clinical reasoning',
};

const DATA_TOOL_IDS = new Set([
  'get_patient_context',
  'get_patient_demographics',
  'get_medications',
  'get_conditions',
  'get_observations',
  'get_encounters',
  'get_allergies',
  'get_procedures',
  'get_immunizations',
  'get_diagnostic_reports',
  'get_care_plans',
  'get_family_history',
  'get_fhir_patient',
  'get_latest_assessment',
]);

function humanizeToolName(toolId: string): string {
  return TOOL_NOUNS[toolId] || toolId.replace(/_/g, ' ').trim();
}

function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] || '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function buildCompletedSummary(toolsUsed: string[], events: NormalizedThinkingEvent[]): string | null {
  const allToolIds = Array.from(
    new Set(
      [
        ...toolsUsed,
        ...events.map((event) => String(event.toolName || '').trim()),
      ].filter(Boolean),
    ),
  );

  if (allToolIds.length === 0) return null;

  const hasReasoning = allToolIds.includes('multi_agent_reasoning');
  const hasReferences = allToolIds.includes('march_rag_retrieval');
  const dataTools = allToolIds.filter((toolId) => DATA_TOOL_IDS.has(toolId));
  const otherTools = allToolIds.filter(
    (toolId) => !DATA_TOOL_IDS.has(toolId)
      && toolId !== 'multi_agent_reasoning'
      && toolId !== 'march_rag_retrieval',
  );

  if (hasReasoning && hasReferences && dataTools.length > 0) {
    return 'Analyzed chart data with clinical references';
  }
  if (hasReasoning && dataTools.length > 0) {
    return 'Analyzed chart data';
  }
  if (hasReferences && dataTools.length > 0) {
    return 'Reviewed chart and references';
  }
  if (hasReasoning && hasReferences) {
    return 'Completed reasoning with references';
  }
  if (hasReasoning) {
    return 'Completed clinical reasoning';
  }
  if (hasReferences) {
    return 'Looked up clinical references';
  }

  if (dataTools.length > 0 && otherTools.length === 0) {
    if (dataTools.length <= 3) {
      return `Reviewed ${joinLabels(dataTools.map(humanizeToolName))}`;
    }
    return 'Reviewed chart data';
  }

  if (allToolIds.length === 1) {
    const [toolId] = allToolIds;
    return `Used ${humanizeToolName(toolId)}`;
  }

  if (allToolIds.length <= 3) {
    return `Used ${joinLabels(allToolIds.map(humanizeToolName))}`;
  }

  return `Completed ${allToolIds.length} execution steps`;
}

function buildOrderedToolExecution(toolsUsed: string[], events: NormalizedThinkingEvent[]): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();

  for (const event of events) {
    const toolId = String(event.toolName || '').trim();
    if (!toolId || seen.has(toolId)) continue;
    if (event.status === 'running') continue;
    seen.add(toolId);
    ordered.push(toolId);
  }

  for (const toolId of toolsUsed) {
    const normalized = String(toolId || '').trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    ordered.push(normalized);
  }

  return ordered;
}

export function ThinkingPanel({
  events,
  metrics,
  status,
  isStreaming,
  thinkingComplete: _thinkingComplete = false,
  streamPhase: _streamPhase,
  warningCount,
  toolsUsedCount,
  toolsUsed,
  currentStepName,
}: ThinkingPanelProps) {
  // #179: this panel used to auto-expand the instant events arrived mid-run
  // (see the removed "isStreaming && events.length > 0" effect below), which
  // put it back in "streaming/expanded" state for the whole run and only
  // collapsed it once the run finished. The #115 default-collapse mechanism
  // used by every other Section-2 shell (S2Card.tsx: `useState(defaultExpanded
  // = false)`, no effect ever forces it back open) is widened here instead of
  // adding a second parallel flag: isExpanded simply starts false and is only
  // ever flipped by the user's own toggle, so the panel stays collapsed for
  // the whole run and remains collapsed when the final answer lands.
  const [isExpanded, setIsExpanded] = useState(false);
  const prevStreamingRef = useRef(isStreaming);
  const userToggledRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Safety net only: if isExpanded were ever true while a run finishes without
  // the user having explicitly toggled it (not reachable today now that the
  // auto-expand effect above is gone, but kept so a future auto-open path
  // can't silently leave the panel expanded post-completion).
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming && !userToggledRef.current) {
      setIsExpanded(false);
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    if (isExpanded && contentRef.current) {
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContentHeight(entry.contentRect.height);
        }
      });
      ro.observe(contentRef.current);
      return () => ro.disconnect();
    }
  }, [isExpanded]);

  if (events.length === 0 && !isStreaming) return null;

  const handleToggle = useCallback(() => {
    userToggledRef.current = true;
    setIsExpanded((v) => !v);
  }, []);

  const elapsed = metrics.totalElapsedMs > 0
    ? metrics.totalElapsedMs
    : metrics.sessionStartAt
      ? Math.max(0, Date.now() - new Date(metrics.sessionStartAt).getTime())
      : 0;

  const isDone = !isStreaming && status !== 'running';
  const hasFailed = status === 'failed' || status === 'blocked';
  const completedSummary = buildCompletedSummary(toolsUsed, events);
  const orderedTools = buildOrderedToolExecution(toolsUsed, events);

  const headerLabel = (() => {
    if (isStreaming && currentStepName) return currentStepName;
    if (isStreaming) return 'Thinking…';
    if (hasFailed) return 'Execution failed';
    if (isDone && completedSummary) return completedSummary;
    if (isDone && toolsUsedCount > 0) return `Completed ${toolsUsedCount} tool${toolsUsedCount > 1 ? 's' : ''}`;
    if (isDone) return 'Reasoning complete';
    return 'Processing…';
  })();

  const stepsCount = events.length;

  return (
    <div className="thinking-panel group/panel" data-testid="thinking-panel">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        className="flex items-center gap-2 w-full text-left py-2 px-2 rounded-lg
          text-text-tertiary hover:text-foreground hover:bg-surface-sunken/50
          transition-all duration-150"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ease-out
            ${isExpanded ? 'rotate-90' : ''}`}
        />

        {isStreaming ? (
          <span className="thinking-pulse-dot" />
        ) : hasFailed ? (
          <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
        ) : warningCount > 0 ? (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
        ) : isDone ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 thinking-check-appear" />
        ) : (
          <Sparkles className="w-3.5 h-3.5 text-primary/70 shrink-0" />
        )}

        <span className="text-xs font-medium truncate">{headerLabel}</span>

        {stepsCount > 0 && isDone && (
          // #158: a bare number here reads as a record count of whatever
          // resource headerLabel names (e.g. "Reviewed encounters" + "16"
          // looked like 16 encounters, not 16 SSE thinking-execution steps).
          // Spelling out the unit disambiguates it regardless of headerLabel.
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-sunken text-text-tertiary font-medium tabular-nums">
            {stepsCount} step{stepsCount !== 1 ? 's' : ''}
          </span>
        )}

        <span className="flex-1" />

        {elapsed > 0 && (
          <span className="text-[10px] tabular-nums opacity-50 shrink-0 font-mono">
            {formatDuration(elapsed)}
          </span>
        )}
        {warningCount > 0 && (
          <span className="text-[10px] text-amber-500 shrink-0 ml-1.5 font-medium">
            {warningCount} warning{warningCount > 1 ? 's' : ''}
          </span>
        )}
      </button>

      <div
        className="overflow-hidden transition-all duration-250 ease-out"
        style={{ maxHeight: isExpanded ? (contentHeight || 600) + 16 : 0, opacity: isExpanded ? 1 : 0 }}
      >
        <div ref={contentRef} className="pl-3 ml-[7px] border-l-2 border-border/40 pt-1 pb-2">
          {orderedTools.length > 0 && (
            <div className="mb-2 mr-2 rounded-lg border border-border/60 bg-surface-sunken/20 px-2.5 py-2">
              <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-text-tertiary/80">
                Execution path
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {orderedTools.map((toolId, index) => (
                  <div key={`${toolId}-${index}`} className="flex items-center gap-1.5">
                    <span className="rounded-md border border-border/60 bg-background px-2 py-1 text-[10px] text-foreground/85">
                      {humanizeToolName(toolId)}
                    </span>
                    {index < orderedTools.length - 1 && (
                      <span className="text-[10px] text-text-tertiary/60">{'->'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <ThinkingStepList
            events={events}
            isStreaming={isStreaming}
          />
        </div>
      </div>
    </div>
  );
}
